import React, { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PreQualLandingPage, { isPreQualLandingAutoAdvanceOrigin } from './PreQualLandingPage';
import PreQualLandingPageV2 from './PreQualLandingPageV2';
import {
  BOAT_TRADER_SOURCE_ID,
  isProduction,
  PREQUAL_LANDING_COMBINED_TARGET_URL,
} from '../constants';
import { LoanPurpose } from '../schema';
import { buildSafeApplyHref } from '../utils/safeApplyHref';

export const PREQUAL_LANDING_V2_VERSION_PARAM = 'v2-prequal';

const PreQualLandingRoute: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const version = new URLSearchParams(location.search).get('version');
  const isV2 = version === PREQUAL_LANDING_V2_VERSION_PARAM;

  if (version && !isV2) {
    console.error(`[PreQualLandingRoute] Unrecognized version param: ${version}`);
  }

  const handleGetPreQualified = useCallback(() => {
    // This landing page is exclusively BoatTrader-branded (see
    // PREQUAL_LANDING_STATIC_SOURCE), so guarantee source/purpose attribution
    // into the Combined funnel even when the incoming URL omits them.
    const params = new URLSearchParams(location.search);
    if (!params.has('source')) params.set('source', String(BOAT_TRADER_SOURCE_ID));
    if (!params.has('purpose')) params.set('purpose', LoanPurpose.boat);

    // PREQUAL_LANDING_COMBINED_TARGET_URL is the prodTestingInQA-tagged production
    // product in every environment (see constants.ts), so non-prod must add
    // prodTesting=true to load the real production LendAPI config — same convention
    // as portalCtaRedirect.ts.
    if (!isProduction()) {
      params.set('prodTesting', 'true');
    } else {
      params.delete('prodTesting');
    }

    const search = params.toString();
    const targetPathAndSearch = `${PREQUAL_LANDING_COMBINED_TARGET_URL}${
      search ? `?${search}` : ''
    }`;

    // Hard-navigates instead of history.push for the same case shouldAutoAdvance fires on --
    // that happens before Routes.tsx resolves the real history basename, corrupting history.push.
    if (isPreQualLandingAutoAdvanceOrigin(params.get('detailedPageName'))) {
      window.location.assign(buildSafeApplyHref(targetPathAndSearch));
      return;
    }

    history.push(targetPathAndSearch);
  }, [history, location.search]);

  return isV2 ? (
    <PreQualLandingPageV2 onGetPreQualified={handleGetPreQualified} />
  ) : (
    <PreQualLandingPage onGetPreQualified={handleGetPreQualified} />
  );
};

export default PreQualLandingRoute;
